import { cn } from '@heidi/utils';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { nextCompatibleWorkItemIds, workProjectIdsById } from '../../data';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspacePane } from './ObjectWorkspacePane';
import type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceAnchorSelection,
  ObjectWorkspaceDocumentsState,
  ObjectWorkspaceFacet,
  ObjectWorkspaceObject,
  ObjectWorkspacePatientDetailsState,
  ObjectWorkspacePatientSessionsState,
  ObjectWorkspaceProjectSelection,
  ObjectWorkspaceTask,
  ObjectWorkspaceTaskSelection,
  ObjectWorkspaceTasksState,
} from './objectWorkspace.data';

export interface ObjectWorkspaceWorkbenchContext {
  object: ObjectWorkspaceObject;
  facet: ObjectWorkspaceFacet;
  anchorSelection?: ObjectWorkspaceAnchorSelection;
  taskSelection?: ObjectWorkspaceTaskSelection;
  /** Open the Heidi chat that already owns a linked Next Steps task. */
  projectSelection?: ObjectWorkspaceProjectSelection;
  /** Unstages every selected task, e.g. once a chat has taken them. */
  clearTaskSelection: () => void;
  newTabTitle: string;
}

export interface ObjectWorkspaceShellProps {
  /** Real identity supplied by the patient or visit selection entry point. */
  object: ObjectWorkspaceObject;
  /** Wait for the owning route to replace `object` before changing identity. */
  isObjectControlled?: boolean;
  /** Optional app-owned workbench content. */
  children?: ReactNode;
  /**
   * App-resolved patient profile state. Required for patient Details; omit on
   * visits (no Details facet) or while the host has nothing to show.
   */
  patientDetailsState?: ObjectWorkspacePatientDetailsState;
  /** App-resolved patient sessions for the Sessions facet; omit on visits. */
  patientSessionsState?: ObjectWorkspacePatientSessionsState;
  /** App-resolved open tasks for Next Steps. */
  tasksState?: ObjectWorkspaceTasksState;
  /** App-resolved documents for the Documents facet. */
  documentsState?: ObjectWorkspaceDocumentsState;
  /** Read-only host summary; omitted or blank hides the summary block. */
  summary?: string;
  /** App-owned Work chat composition. Takes precedence over `children`. */
  renderWorkbench?: (context: ObjectWorkspaceWorkbenchContext) => ReactNode;
  className?: string;
  onClose: () => void;
  onObjectChange?: (object: ObjectWorkspaceObject) => void;
  /** Opens the host-owned patient attachment flow when an encounter has no patient. */
  onAddPatient?: (anchorElement: HTMLElement) => void;
  /** Whether an explicit user action currently owns the context panel. */
  isContextPanelOpen?: boolean;
  /** Same-kind peers from the mounted Home data source (empty when alone). */
  switcherOptions: readonly ObjectWorkspaceObject[];
  onAnchorSelect?: (anchor: ObjectWorkspaceAnchor) => void;
  onDocumentSelect?: (document: ObjectWorkspaceAnchor) => void;
}

type ObjectWorkspaceContentProps = Omit<
  ObjectWorkspaceShellProps,
  'isObjectControlled' | 'onObjectChange'
> & {
  onObjectChange: (object: ObjectWorkspaceObject) => void;
};

/**
 * Persistent patient/visit workspace chrome. The primary navigation rail stays
 * app-owned immediately outside this component; this shell occupies the Home
 * content slot beside it and keeps the object pane fixed next to a flexible
 * workbench surface.
 */
function ObjectWorkspaceContent({
  object,
  children,
  patientDetailsState,
  patientSessionsState,
  tasksState,
  documentsState,
  summary,
  renderWorkbench,
  className,
  onClose,
  onObjectChange,
  onAddPatient,
  isContextPanelOpen = false,
  switcherOptions: suppliedSwitcherOptions,
  onAnchorSelect,
  onDocumentSelect,
}: ObjectWorkspaceContentProps): JSX.Element {
  const intl = useIntl();
  const [activeFacet, setActiveFacet] = useState<ObjectWorkspaceFacet>('work');
  const [anchorSelection, setAnchorSelection] =
    useState<ObjectWorkspaceAnchorSelection>();
  const [taskPick, setTaskPick] = useState<{ ids: readonly string[]; sequence: number }>({
    ids: [],
    sequence: 0,
  });
  const [projectSelection, setProjectSelection] =
    useState<ObjectWorkspaceProjectSelection>();
  const tasks = useMemo<readonly ObjectWorkspaceTask[]>(
    () => (tasksState?.status === 'ready' ? tasksState.tasks : []),
    [tasksState]
  );
  const selectedTasks = useMemo(
    () => taskPick.ids.flatMap((id) => tasks.filter((task) => task.id === id)),
    [taskPick.ids, tasks]
  );
  const selectedTaskIds = useMemo(
    () => selectedTasks.map((task) => task.id),
    [selectedTasks]
  );
  const taskSelection = useMemo<ObjectWorkspaceTaskSelection | undefined>(
    () =>
      taskPick.sequence === 0
        ? undefined
        : {
            launches: selectedTasks.map((task) => task.launch),
            prompt: selectedTasks
              .map((task) => task.launch.title.trim())
              .filter(Boolean)
              .join(', '),
            sequence: taskPick.sequence,
          },
    [selectedTasks, taskPick.sequence]
  );

  const switcherOptions = useMemo(
    () => [
      object,
      ...suppliedSwitcherOptions.filter((option) => option.id !== object.id),
    ],
    [object, suppliedSwitcherOptions]
  );

  const handleObjectChange = (nextObject: ObjectWorkspaceObject) => {
    onObjectChange(nextObject);
  };

  const handleAnchorSelect = (anchor: ObjectWorkspaceAnchor) => {
    setAnchorSelection((current) => ({
      anchor,
      prompt: intl.formatMessage(messages.pullUpSession, { title: anchor.title }),
      sequence: (current?.sequence ?? 0) + 1,
    }));
    onAnchorSelect?.(anchor);
  };

  const handleTaskToggle = (task: ObjectWorkspaceTask) => {
    const workProjectId = task.launch.workProjectId?.trim();
    if (workProjectId) {
      setProjectSelection((current) => ({
        projectId: workProjectId,
        title: task.launch.title.trim() || task.title,
        sequence: (current?.sequence ?? 0) + 1,
      }));
      return;
    }
    setTaskPick((current) => ({
      ids: nextCompatibleWorkItemIds(selectedTaskIds, task.id, workProjectIdsById(tasks)),
      sequence: current.sequence + 1,
    }));
  };

  const clearTaskSelection = useCallback(() => {
    setTaskPick((current) =>
      current.ids.length === 0 ? current : { ids: [], sequence: current.sequence }
    );
  }, []);

  return (
    <section
      aria-label={intl.formatMessage(messages.workspaceLabel, {
        name: object.name,
      })}
      className={cn(
        'flex size-full min-h-0 overflow-hidden bg-surface-accent md:gap-4 md:p-4',
        className
      )}
      data-testid="object-workspace-shell"
    >
      <ObjectWorkspacePane
        object={object}
        patientDetailsState={object.kind === 'patient' ? patientDetailsState : undefined}
        patientSessionsState={
          object.kind === 'patient' ? patientSessionsState : undefined
        }
        tasksState={tasksState}
        documentsState={documentsState}
        selectedTaskIds={selectedTaskIds}
        summary={summary}
        isHidden={isContextPanelOpen}
        switcherOptions={switcherOptions}
        onClose={onClose}
        onObjectChange={handleObjectChange}
        onAddPatient={onAddPatient}
        onFacetChange={setActiveFacet}
        onAnchorSelect={handleAnchorSelect}
        onDocumentSelect={onDocumentSelect}
        onTaskToggle={handleTaskToggle}
      />
      <div
        aria-label={intl.formatMessage(messages.workbenchLabel, {
          name: object.name,
        })}
        className="hidden min-w-0 flex-1 overflow-hidden md:block"
        data-testid="object-workspace-workbench"
      >
        {renderWorkbench
          ? renderWorkbench({
              object,
              facet: activeFacet,
              anchorSelection,
              taskSelection,
              projectSelection,
              clearTaskSelection,
              newTabTitle: intl.formatMessage(messages.newThread),
            })
          : children}
      </div>
    </section>
  );
}

export function ObjectWorkspaceShell({
  object,
  isObjectControlled = false,
  onObjectChange,
  ...contentProps
}: ObjectWorkspaceShellProps): JSX.Element {
  const [uncontrolledObject, setUncontrolledObject] = useState(object);

  useEffect(() => {
    setUncontrolledObject(object);
  }, [object]);

  const activeObject = isObjectControlled ? object : uncontrolledObject;
  const handleObjectChange = (nextObject: ObjectWorkspaceObject) => {
    if (!isObjectControlled) setUncontrolledObject(nextObject);
    onObjectChange?.(nextObject);
  };

  return (
    <ObjectWorkspaceContent
      key={`${activeObject.kind}:${activeObject.id}`}
      {...contentProps}
      object={activeObject}
      onObjectChange={handleObjectChange}
    />
  );
}

export type {
  ObjectWorkspaceAnchor,
  ObjectWorkspaceAnchorSelection,
  ObjectWorkspaceDocumentsState,
  ObjectWorkspaceFacet,
  ObjectWorkspaceKind,
  ObjectWorkspaceObject,
  ObjectWorkspacePatientDetails,
  ObjectWorkspacePatientDetailsState,
  ObjectWorkspacePatientSessionsState,
  ObjectWorkspaceProjectSelection,
  ObjectWorkspaceTask,
  ObjectWorkspaceTaskSelection,
  ObjectWorkspaceTasksState,
} from './objectWorkspace.data';
